import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axiosClient from '../services/setupInterceptors';

const WISHLIST_STORAGE_KEY = 'guestWishlist';

const getInitialItems = () => {
  try {
    const serializedWishlist = localStorage.getItem(WISHLIST_STORAGE_KEY);
    return serializedWishlist ? JSON.parse(serializedWishlist) : [];
  } catch (e) {
    console.error("Could not load wishlist from localStorage", e);
    return [];
  }
};

const updateLocalStorage = (items) => {
  localStorage.setItem(WISHLIST_STORAGE_KEY, JSON.stringify(items));
};

const wishlistApiThunk = async (url, method, body, { rejectWithValue }) => {
  try {
    const response = await axiosClient({ url, method, data: body });
    return response.data.data;
  } catch (err) {
    return rejectWithValue(err.response?.data || err.message);
  }
};

export const fetchWishlistFromServer = createAsyncThunk(
  'wishlist/fetchFromServer',
  async (_, { rejectWithValue }) => {
    return wishlistApiThunk('/wishlist', 'GET', null, { rejectWithValue });
  }
);

export const mergeLocalWishlistOnLogin = createAsyncThunk(
  'wishlist/mergeOnLogin',
  async (_, { getState, rejectWithValue }) => {
    const localItems = getState().wishlist.items.map(item => item.product_id);
    return wishlistApiThunk('/wishlist/merge/all', 'POST',  localItems, { rejectWithValue });
  }
);

export const addItemToWishlistServer = createAsyncThunk(
  'wishlist/addItemToWishlistServer',
  async (productId, { rejectWithValue }) => {
    return wishlistApiThunk(`/wishlist/${productId}`, 'POST', null, { rejectWithValue });
  }
);

export const removeItemFromWishlistServer = createAsyncThunk(
  'wishlist/removeItemFromWishlistServer',
  async (productId, { rejectWithValue }) => {
    return wishlistApiThunk(`/wishlist/${productId}`, 'DELETE', null, { rejectWithValue });
  }
);

export const notifyMeServer = createAsyncThunk(
  'wishlist/notifyMeServer',
  async (productId, { rejectWithValue }) => {
    return wishlistApiThunk(`/notify_me/${productId}`, 'POST', null, { rejectWithValue });
  }
);

const initialState = {
  items: getInitialItems(),
  loading: false,
  status: 'idle',
  error: null,
  message: null,
};

const wishlistSlice = createSlice({
  name: 'wishlist',
  initialState,

  reducers: {
    updateLocalWishlist(state, { payload }) {
      const { action, product } = payload;
      let newItems = [...state.items];

      if (action === 'ADD') {
        if (!newItems.find(i => i.product_id === product.id)) {
          newItems.push({ product_id: product.id, product });
        }
      } else if (action === 'REMOVE') {
        newItems = newItems.filter(i => i.product_id !== product.id);
      }

      state.items = newItems;
      updateLocalStorage(newItems);
    },

    clearLocalWishlistData(state) {
      state.items = [];
      state.message = null;
      localStorage.removeItem(WISHLIST_STORAGE_KEY);
    },
  },

  extraReducers: (builder) => {
    const handleFulfilled = (state, { payload }) => {
      state.status = 'idle';
      state.loading = false;
      state.error = null;
      state.items = payload;
      updateLocalStorage(state.items);
      if (payload?.status === 'SUCCESS') {
        localStorage.removeItem(WISHLIST_STORAGE_KEY);
      }
    };

    const handlePending = (state, action) => {
      state.loading = true;
      state.status = action.type.includes('mergeOnLogin') ? 'syncing' : 'loading';
      state.error = null;
    };

    const handleRejected = (state, { payload }) => {
      state.loading = false;
      state.status = 'failed';
      state.error = payload;
    };

    builder
      .addCase(fetchWishlistFromServer.pending, handlePending)
      .addCase(fetchWishlistFromServer.fulfilled, handleFulfilled)
      .addCase(fetchWishlistFromServer.rejected, handleRejected)

      .addCase(mergeLocalWishlistOnLogin.pending, handlePending)
      .addCase(mergeLocalWishlistOnLogin.fulfilled, handleFulfilled)
      .addCase(mergeLocalWishlistOnLogin.rejected, handleRejected)

      .addCase(addItemToWishlistServer.pending, handlePending)
      .addCase(addItemToWishlistServer.fulfilled, handleFulfilled)
      .addCase(addItemToWishlistServer.rejected, handleRejected)

      .addCase(removeItemFromWishlistServer.pending, handlePending)
      .addCase(removeItemFromWishlistServer.fulfilled, handleFulfilled)
      .addCase(removeItemFromWishlistServer.rejected, handleRejected)

      .addCase(notifyMeServer.pending, handlePending)
      .addCase(notifyMeServer.fulfilled, (state, { payload }) => {
        state.loading = false;
        state.message = payload?.message || "Notification registered";
      })
      .addCase(notifyMeServer.rejected, handleRejected);
  },
});

export const { updateLocalWishlist, clearLocalWishlistData } = wishlistSlice.actions;


export default wishlistSlice.reducer;
